import { startTransition, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { ContentCard, Row, Button, Chip, Alert } from "@narmi/design_system";
import { dollars } from "byzantine";
import Filters from "byzantine/src/filters";
import HeaderWithSteps from "../../HeaderWithSteps";
import Container from "../../Container";
import { useACHPassthroughImportContext } from "../ACHPassthroughImportContext";
import {
  formatCurrencyFromCents,
  formatEffectiveDateRange,
} from "./passthroughFormat";
import styles from "./ValidationResultsScreen.module.scss";

type ValidationEntry = API.ACHPassthrough.ValidationEntry;
type FileSummary = API.ACHPassthrough.FileSummary;
type L10n = ReturnType<typeof useLocalization>["l10n"];

type CheckKey =
  | "file_format"
  | "company_id"
  | "sec_codes"
  | "send_limit"
  | "collect_limit"
  | "origin_name"
  | "origin_routing_number"
  | "effective_date";

const CHECK_ORDER: CheckKey[] = [
  "file_format",
  "company_id",
  "sec_codes",
  "send_limit",
  "collect_limit",
  "origin_name",
  "origin_routing_number",
  "effective_date",
];

const CHECK_LABEL_KEYS: Record<CheckKey, string> = {
  file_format: "ach-passthrough-validation-check-file-format",
  company_id: "ach-passthrough-validation-check-company-id",
  sec_codes: "ach-passthrough-validation-check-sec-codes",
  send_limit: "ach-passthrough-validation-check-send-limit",
  collect_limit: "ach-passthrough-validation-check-collect-limit",
  origin_name: "ach-passthrough-validation-check-origin-name",
  origin_routing_number: "ach-passthrough-validation-check-origin-routing",
  effective_date: "ach-passthrough-validation-check-effective-date",
};

const FILE_FORMAT_CODES = new Set([
  "nacha_parse_failed",
  "invalid_characters",
  "invalid_mime_type",
  "file_too_large",
]);
const EFFECTIVE_DATE_CODES = new Set([
  "effective_date_in_past",
  "effective_date_missing",
  "effective_date_past_cutoff",
  "effective_date_too_far",
  "same_day_not_allowed",
]);

const matchesCheck = (key: CheckKey, entry: ValidationEntry): boolean => {
  switch (key) {
    case "file_format":
      return FILE_FORMAT_CODES.has(entry.code);
    case "company_id":
      return entry.code === "invalid_company_id";
    case "sec_codes":
      return entry.code === "invalid_sec_code";
    case "send_limit":
      return (
        entry.code === "send_limit_exceeded" ||
        (entry.code === "limit_not_configured" &&
          entry.context?.direction === "send")
      );
    case "collect_limit":
      return (
        entry.code === "collect_limit_exceeded" ||
        (entry.code === "limit_not_configured" &&
          entry.context?.direction === "collect")
      );
    case "origin_name":
      return entry.code === "invalid_origin_name";
    case "origin_routing_number":
      return entry.code === "invalid_origin_routing";
    case "effective_date":
      return EFFECTIVE_DATE_CODES.has(entry.code);
    default:
      return false;
  }
};

const OTHER_ERROR_LABEL_KEYS: Record<string, string> = {
  duplicate_file: "ach-passthrough-validation-check-duplicate-file",
  payment_count_exceeded: "ach-passthrough-validation-check-payment-count",
  return_entry_not_allowed: "ach-passthrough-validation-check-return-entries",
  balanced_file_failed: "ach-passthrough-validation-check-file-balance",
  balanced_batch_failed: "ach-passthrough-validation-check-batch-balance",
  unbalanced_file_not_allowed: "ach-passthrough-validation-check-file-balance",
  missing_send_permission: "ach-passthrough-validation-check-send-permission",
  missing_collect_permission:
    "ach-passthrough-validation-check-collect-permission",
  offset_account_not_found: "ach-passthrough-validation-check-offset-account",
  offset_account_not_accessible:
    "ach-passthrough-validation-check-offset-account",
  invalid_destination_name: "ach-passthrough-validation-check-destination-name",
  invalid_destination_routing:
    "ach-passthrough-validation-check-destination-routing",
};

const formatCurrencyFromDollarString = (amount: string): string =>
  Filters.currency(dollars(amount), { hasDecimal: true });

const formatListWithMore = (
  values: string[],
  l10n: L10n,
  visibleCount = 2
): string => {
  if (values.length <= visibleCount) return values.join(", ");
  const remaining = values.length - visibleCount;
  const more = l10n.getString("ach-passthrough-validation-more-count", {
    count: remaining,
  });
  return `${values.slice(0, visibleCount).join(", ")}, ${more}`;
};

const LimitErrorDetail = ({
  entry,
  l10n,
}: {
  entry: ValidationEntry;
  l10n: L10n;
}) => {
  const context = entry.context as {
    requested?: string;
    binding_window?: {
      cap: string;
      headroom: string;
      window_business_days: number;
    };
  };

  if (!context.binding_window) {
    return (
      <span className="fontSize--xs fontColor--secondary">{entry.message}</span>
    );
  }

  return (
    <>
      {context.requested && (
        <div className="fontWeight--semibold">
          {formatCurrencyFromDollarString(context.requested)}
        </div>
      )}
      <div className="fontSize--xs fontColor--secondary">
        {l10n.getString("ach-passthrough-validation-limit-detail", {
          cap: formatCurrencyFromDollarString(context.binding_window.cap),
          windowLabel: l10n.getString(
            "ach-passthrough-validation-window-label",
            {
              windowDays: context.binding_window.window_business_days,
            }
          ),
          remaining: formatCurrencyFromDollarString(
            context.binding_window.headroom
          ),
        })}
      </div>
    </>
  );
};

const ExpectedListDetail = ({
  entry,
  contextKey,
  rowKey,
  expandedKeys,
  onToggle,
  l10n,
}: {
  entry: ValidationEntry;
  contextKey: string;
  rowKey: string;
  expandedKeys: Set<string>;
  onToggle: (key: string) => void;
  l10n: L10n;
}) => {
  const validValues = entry.context?.[contextKey];
  const isExpanded = expandedKeys.has(rowKey);

  return (
    <>
      <div className="fontSize--xs fontColor--secondary">{entry.message}</div>
      {Array.isArray(validValues) && validValues.length > 0 && (
        <>
          {isExpanded ? (
            <div className="fontSize--xs fontColor--secondary">
              {validValues.join(", ")}
            </div>
          ) : (
            <Button
              kind="plain"
              size="xs"
              label={l10n.getString("ach-passthrough-validation-show-expected")}
              onClick={() => onToggle(rowKey)}
            />
          )}
        </>
      )}
    </>
  );
};

const ErrorDetail = ({
  checkKey,
  entry,
  entryKey,
  expandedKeys,
  onToggle,
  l10n,
}: {
  checkKey: CheckKey | null;
  entry: ValidationEntry;
  entryKey: string;
  expandedKeys: Set<string>;
  onToggle: (key: string) => void;
  l10n: L10n;
}) => {
  if (checkKey === "send_limit" || checkKey === "collect_limit") {
    return <LimitErrorDetail entry={entry} l10n={l10n} />;
  }
  if (checkKey === "company_id") {
    return (
      <ExpectedListDetail
        entry={entry}
        contextKey="valid_company_ids"
        rowKey={entryKey}
        expandedKeys={expandedKeys}
        onToggle={onToggle}
        l10n={l10n}
      />
    );
  }
  if (checkKey === "sec_codes") {
    return (
      <ExpectedListDetail
        entry={entry}
        contextKey="valid_sec_codes"
        rowKey={entryKey}
        expandedKeys={expandedKeys}
        onToggle={onToggle}
        l10n={l10n}
      />
    );
  }
  return (
    <span className="fontSize--xs fontColor--secondary">{entry.message}</span>
  );
};

type Row = {
  key: string;
  labelKey: string;
  passed: boolean;
  passedValue?: string | null;
  entries: ValidationEntry[];
  checkKey: CheckKey | null;
};

const passedValueForCheck = (
  key: CheckKey,
  fileSummary: FileSummary,
  l10n: L10n
): string | null => {
  switch (key) {
    case "file_format":
      return "";
    case "company_id":
      return formatListWithMore(fileSummary.company_ids, l10n);
    case "sec_codes":
      return fileSummary.sec_codes.join(", ");
    case "origin_name":
      return fileSummary.origin_name;
    case "origin_routing_number":
      return fileSummary.origin_routing_number;
    case "effective_date":
      return formatEffectiveDateRange(fileSummary);
    default:
      return null;
  }
};

const buildCanonicalRow = (
  key: CheckKey,
  errors: ValidationEntry[],
  fileSummary: FileSummary | null,
  l10n: L10n
): Row | null => {
  const entries = errors.filter((entry) => matchesCheck(key, entry));

  if (entries.length > 0 || !fileSummary) {
    return {
      key,
      labelKey: CHECK_LABEL_KEYS[key],
      passed: entries.length === 0,
      entries,
      checkKey: key,
    };
  }

  const passedValue = passedValueForCheck(key, fileSummary, l10n);
  if (passedValue === null) return null;

  return {
    key,
    labelKey: CHECK_LABEL_KEYS[key],
    passed: true,
    passedValue,
    entries,
    checkKey: key,
  };
};

const buildRows = (
  errors: ValidationEntry[],
  fileSummary: FileSummary | null,
  l10n: L10n
): Row[] => {
  const canonicalChecks: CheckKey[] = fileSummary
    ? CHECK_ORDER
    : ["file_format"];
  const canonicalRows = canonicalChecks
    .map((key) => buildCanonicalRow(key, errors, fileSummary, l10n))
    .filter((row): row is Row => row !== null);

  const matchedCodes = new Set(
    canonicalRows.flatMap((row) => row.entries.map((entry) => entry.code))
  );
  const otherRows = errors
    .filter((entry) => !matchedCodes.has(entry.code))
    .map(
      (entry, index): Row => ({
        key: `other-${entry.code}-${entry.field}-${index}`,
        labelKey:
          OTHER_ERROR_LABEL_KEYS[entry.code] ??
          "ach-passthrough-validation-check-other",
        passed: false,
        entries: [entry],
        checkKey: null,
      })
    );

  return [...canonicalRows, ...otherRows];
};

const ValidationResultsScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { validateResponse, fileName, clearValidation } =
    useACHPassthroughImportContext();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  if (!validateResponse) {
    return <Navigate to="../.." replace />;
  }

  const {
    is_valid: isValid,
    file_summary: fileSummary,
    errors,
  } = validateResponse;
  const rows = buildRows(errors, fileSummary, l10n);
  const passedRows = rows.filter((row) => row.passed);
  const errorRows = rows.filter((row) => !row.passed);
  const hasUnexplainedFailure = !isValid && errorRows.length === 0;

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderCheckRow = (row: Row) => (
    <div key={row.key} className={styles.checkRow}>
      <span className={`${styles.checkRowLabel} fontSize--s`}>
        {l10n.getString(row.labelKey)}
      </span>
      {row.passed ? (
        <span
          className={`${styles.checkRowValue} fontSize--s fontWeight--semibold`}
        >
          {row.checkKey === "file_format"
            ? l10n.getString("ach-passthrough-validation-value-valid")
            : row.passedValue}
        </span>
      ) : (
        <div className={styles.checkRowValue}>
          <Chip
            kind="error"
            label={l10n.getString("ach-passthrough-validation-value-failed")}
          />
          <div className={styles.checkRowDetail}>
            {row.entries.map((entry, index) => (
              <ErrorDetail
                key={`${row.key}-${index}`}
                checkKey={row.checkKey}
                entry={entry}
                entryKey={`${row.key}-${index}`}
                expandedKeys={expandedKeys}
                onToggle={toggleExpanded}
                l10n={l10n}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const handleRetryUpload = () => {
    // Must share a transition with navigate() (react-router wraps it in one internally),
    // or clearValidation() flushes first and the stale-validateResponse redirect above
    // fires before the URL changes, clobbering the preselectMode state below.
    startTransition(() => {
      clearValidation();
      navigate("../..", { state: { preselectMode: "ach_passthrough" } });
    });
  };

  const handleCancel = () => {
    startTransition(() => {
      clearValidation();
      navigate("/transfer");
    });
  };

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        useDisplayFont
        headerText={l10n.getString("ach-passthrough-validation-title")}
      />
      <div className="margin--top--l">
        <ContentCard kind="bordered" paddingSize="l" radiusSize="m">
          <div className={styles.fileSummaryContent}>
            <div className={styles.fileSummaryLeft}>
              <span className="narmi-icon-file-text1" />
              <div className={styles.fileSummaryText}>
                <div className={`${styles.fileName} fontWeight--semibold`}>
                  {fileName}
                </div>
                {fileSummary && (
                  <div className="fontSize--s fontColor--secondary">
                    {l10n.getString(
                      "ach-passthrough-validation-payment-count",
                      {
                        count:
                          fileSummary.payment_credit_count +
                          fileSummary.payment_debit_count,
                      }
                    )}
                  </div>
                )}
              </div>
            </div>
            {fileSummary && (
              <div className={styles.fileSummaryRight}>
                <div className={styles.fileSummaryStat}>
                  <div className="fontSize--xs fontColor--secondary">
                    {l10n.getString("ach-passthrough-validation-send-count", {
                      count: fileSummary.payment_credit_count,
                    })}
                  </div>
                  <div className="fontWeight--semibold">
                    {formatCurrencyFromCents(
                      fileSummary.payment_total_credit_amount
                    )}
                  </div>
                </div>
                <div className={styles.fileSummaryStat}>
                  <div className="fontSize--xs fontColor--secondary">
                    {l10n.getString(
                      "ach-passthrough-validation-collect-count",
                      {
                        count: fileSummary.payment_debit_count,
                      }
                    )}
                  </div>
                  <div className="fontWeight--semibold">
                    {formatCurrencyFromCents(
                      fileSummary.payment_total_debit_amount
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ContentCard>
      </div>

      {hasUnexplainedFailure ? (
        <div className="margin--top--l">
          <Alert isActive kind="error" isDismissable={false}>
            {l10n.getString("file-upload-error-generic")}
          </Alert>
        </div>
      ) : (
        <>
          {errorRows.length > 0 && (
            <div className="margin--top--l">
              <Alert isActive kind="warn" isDismissable={false}>
                {l10n.getString("ach-passthrough-validation-alert")}
              </Alert>
            </div>
          )}

          {passedRows.length > 0 && (
            <div className="margin--top--l">
              <ContentCard kind="bordered" paddingSize="l">
                <span className="fontSize--xs fontWeight--bold fontColor--secondary">
                  {l10n.getString("ach-passthrough-validation-passed-heading")}
                </span>
                {passedRows.map(renderCheckRow)}
              </ContentCard>
            </div>
          )}

          {errorRows.length > 0 && (
            <div className="margin--top--l">
              <ContentCard kind="bordered" paddingSize="l">
                <span
                  className={`${styles.errorsHeading} fontSize--xs fontWeight--bold`}
                >
                  {l10n.getString("ach-passthrough-validation-errors-heading")}
                </span>
                {errorRows.map(renderCheckRow)}
              </ContentCard>
            </div>
          )}
        </>
      )}

      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-cancel")}
              onClick={handleCancel}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("ach-passthrough-validation-retry-upload")}
              onClick={handleRetryUpload}
            />
          </Row.Item>
        </Row>
      </div>
    </Container>
  );
};

export default ValidationResultsScreen;
